"use client";

import { useRef, useState, type CSSProperties } from "react";
import { useHoverCapability } from "@/hooks/useHoverCapability";
import { cn } from "@/lib/utils";

function isRTL(text: string) {
  return /[\u0590-\u05FF\u0600-\u06FF\u0700-\u074F]/.test(text);
}

export interface InfoCardProps {
  image: string;
  title: string;
  description: string;
  width?: number | string;
  height?: number | string;
  borderColor?: string;
  borderBgColor?: string;
  borderWidth?: number;
  borderPadding?: number;
  cardBgColor?: string;
  shadowColor?: string;
  patternColor1?: string;
  patternColor2?: string;
  textColor?: string;
  hoverTextColor?: string;
  fontFamily?: string;
  rtlFontFamily?: string;
  effectBgColor?: string;
  contentPadding?: string;
  className?: string;
  responsive?: boolean;
}

export function InfoCard({
  image,
  title,
  description,
  width = 388,
  height = 378,
  borderColor = "var(--info-card-border-accent, #DAFF3E)",
  borderBgColor = "var(--info-card-border-bg, #242424)",
  borderWidth = 3,
  borderPadding = 14,
  cardBgColor = "var(--info-card-bg, #000)",
  shadowColor = "var(--info-card-shadow, #242424)",
  patternColor1 = "var(--info-card-pattern-1, rgba(230,230,230,0.15))",
  patternColor2 = "var(--info-card-pattern-2, rgba(240,240,240,0.15))",
  textColor = "var(--info-card-text, #f5f5f5)",
  hoverTextColor = "var(--info-card-hover-text, #242424)",
  fontFamily = "var(--info-card-font, var(--font-segoe))",
  rtlFontFamily = "var(--info-card-rtl-font, var(--font-segoe))",
  effectBgColor = "var(--info-card-effect-bg, #DAFF3E)",
  contentPadding = "10px 16px",
  className,
  responsive = false,
}: InfoCardProps) {
  const [hovered, setHovered] = useState(false);
  const borderRef = useRef<HTMLDivElement>(null);
  const canHover = useHoverCapability();
  const showHover = canHover && hovered;

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!canHover) return;
    const border = borderRef.current;
    if (!border) return;
    const rect = border.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    const angle = Math.atan2(y, x);
    border.style.setProperty("--rotation", `${angle}rad`);
  };

  const rtl = isRTL(title) || isRTL(description);
  const effectiveFont = rtl ? rtlFontFamily : fontFamily;
  const titleDirection = isRTL(title) ? "rtl" : "ltr";
  const descDirection = isRTL(description) ? "rtl" : "ltr";

  const pattern =
    `linear-gradient(45deg, ${patternColor1} 25%, transparent 25%, transparent 75%, ${patternColor2} 75%),` +
    `linear-gradient(-45deg, ${patternColor2} 25%, transparent 25%, transparent 75%, ${patternColor1} 75%)`;

  const borderGradient = `conic-gradient(from var(--rotation,0deg), ${borderColor} 0deg, ${borderColor} 90deg, ${borderBgColor} 90deg, ${borderBgColor} 360deg)`;

  const outerStyle: CSSProperties = responsive
    ? {
        width: "100%",
        maxWidth: typeof width === "number" ? width : 388,
        aspectRatio: "388 / 410",
        height: "auto",
        border: `${borderWidth}px solid transparent`,
        borderRadius: "1em",
        backgroundOrigin: "border-box",
        backgroundClip: "padding-box, border-box",
        backgroundImage: `linear-gradient(${cardBgColor}, ${cardBgColor}), ${borderGradient}`,
        padding: borderPadding,
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        userSelect: "none",
        transition: "box-shadow 0.3s",
        position: "relative",
        fontFamily: effectiveFont,
        boxShadow: showHover ? `0 12px 32px ${shadowColor}40` : undefined,
      }
    : {
        width,
        height,
        border: `${borderWidth}px solid transparent`,
        borderRadius: "1em",
        backgroundOrigin: "border-box",
        backgroundClip: "padding-box, border-box",
        backgroundImage: `linear-gradient(${cardBgColor}, ${cardBgColor}), ${borderGradient}`,
        padding: borderPadding,
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        userSelect: "none",
        transition: "box-shadow 0.3s",
        position: "relative",
        fontFamily: effectiveFont,
        boxShadow: showHover ? `0 12px 32px ${shadowColor}40` : undefined,
      };

  return (
    <div
      ref={borderRef}
      {...(canHover
        ? {
            onMouseMove: handleMouseMove,
            onMouseEnter: () => setHovered(true),
            onMouseLeave: () => {
              setHovered(false);
              borderRef.current?.style.setProperty("--rotation", "0deg");
            },
          }
        : {})}
      className={cn(className)}
      style={outerStyle}
    >
      <div
        style={{
          width: responsive ? "100%" : 354,
          height: responsive ? "100%" : 344,
          borderRadius: "1em",
          background: cardBgColor,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
          backgroundImage: pattern,
          backgroundSize: "20.84px 20.84px",
          padding: "0 0 8px 0",
        }}
      >
        <div
          className="relative w-full shrink-0 overflow-hidden"
          style={{ flex: responsive ? "0 0 46%" : "0 0 52%" }}
        >
          <img
            src={image}
            alt={title}
            className="block h-full w-full object-cover"
            loading="lazy"
            decoding="async"
          />
        </div>
        <div
          style={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            gap: 4,
            padding: contentPadding,
            minHeight: 0,
          }}
        >
          <h3
            style={{
              fontSize: "clamp(1rem, 2.5vw + 0.5rem, 21px)",
              fontWeight: "bold",
              letterSpacing: "-.01em",
              lineHeight: "normal",
              marginBottom: 5,
              flexShrink: 0,
              color: showHover ? hoverTextColor : textColor,
              transition: "color 0.3s ease",
              position: "relative",
              overflow: "hidden",
              direction: titleDirection,
              width: "auto",
            }}
          >
            <span
              style={{
                position: "relative",
                zIndex: 10,
                padding: "2px 4px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",
                width: "100%",
                height: "100%",
              }}
            >
              {title}
            </span>
            <span
              aria-hidden
              style={{
                clipPath: showHover
                  ? "polygon(0 0, 100% 0, 100% 100%, 0% 100%)"
                  : "polygon(0 50%, 100% 50%, 100% 50%, 0 50%)",
                transformOrigin: "center",
                transition: "all cubic-bezier(.1,.5,.5,1) 0.4s",
                position: "absolute",
                left: -4,
                right: -4,
                top: -4,
                bottom: -4,
                zIndex: 0,
                backgroundColor: effectBgColor,
              }}
            />
          </h3>
          <p
            style={{
              fontSize: "clamp(0.8125rem, 1.5vw + 0.4rem, 14px)",
              color: textColor,
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              direction: descDirection,
              marginBottom: 0,
              paddingBottom: 0,
              flexShrink: 0,
              minHeight: "calc(3 * 1.45em)",
              lineHeight: 1.45,
            }}
          >
            {description}
          </p>
        </div>
      </div>
    </div>
  );
}
